import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  X,
  Download,
  Sparkles,
  LinkIcon,
  Loader2,
  ImageIcon,
  FolderOpen,
  History,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import MainNavigation from '../components/MainNavigation';
import ThemeToggle from '../components/ThemeToggle';
import ShareableCard from '../components/scorecard/ShareableCard';
import MarkdownContent from '../components/chat/MarkdownContent';
import { useAuth } from '../contexts/AuthContext';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 10;
const MAX_LONG_EDGE = 1920; // downsample limit

// Compress + resize single file to base64 data URL (JPEG 80)
const fileToCompressedDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const max = Math.max(width, height);
        if (max > MAX_LONG_EDGE) {
          const scale = MAX_LONG_EDGE / max;
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const Stepper = ({ step }) => (
  <div className="flex items-center gap-3 mb-10">
    {[1, 2, 3].map((n) => {
      const active = step === n;
      const done = step > n;
      return (
        <React.Fragment key={n}>
          <div
            className={`flex items-center gap-2 ${
              active ? 'text-lime-500' : done ? 'text-obsidian-200' : 'text-obsidian-500'
            }`}
          >
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold tabular-nums transition-all ${
                active
                  ? 'bg-lime-500 text-obsidian-900'
                  : done
                  ? 'bg-lime-500/20 text-lime-500 border border-lime-500/40'
                  : 'bg-white/[0.04] border border-white/10 text-obsidian-400'
              }`}
            >
              {done ? '✓' : n}
            </div>
            <span className="label-micro">
              {n === 1 ? '貼連結' : n === 2 ? '上傳截圖' : '診斷報告'}
            </span>
          </div>
          {n < 3 && <div className="w-10 h-px bg-white/10" />}
        </React.Fragment>
      );
    })}
  </div>
);

const Scorecard = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [videoUrl, setVideoUrl] = useState('');
  const [files, setFiles] = useState([]); // {name, dataUrl}
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [meta, setMeta] = useState(null); // credits info
  const [aspect, setAspect] = useState('1:1');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const fileInputRef = useRef(null);
  const cardRef = useRef(null);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/api/scorecard/list`);
      setHistory(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      toast.error('讀取歷史失敗');
    } finally {
      setLoadingHistory(false);
    }
  };

  const openHistoryDrawer = async () => {
    setHistoryOpen(true);
    await fetchHistory();
  };

  const loadReport = async (id) => {
    try {
      const r = await axios.get(`${API}/api/scorecard/${id}`);
      const doc = r.data;
      if (!doc?.report) {
        toast.error('報告資料不完整');
        return;
      }
      setReport(doc.report);
      setVideoUrl(doc.video_url || '');
      setMeta({
        credits_charged: Number(doc.credits_charged || 0),
        credits_remaining: Number(user?.credits || 0),
        model: doc.model || '',
      });
      setStep(3);
      setHistoryOpen(false);
    } catch (_) {
      toast.error('讀取診斷報告失敗');
    }
  };

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList);
    const room = MAX_FILES - files.length;
    if (arr.length > room) {
      toast.warning(`最多 ${MAX_FILES} 張，已截斷為前 ${room} 張`);
    }
    const limited = arr.slice(0, room);
    const processed = [];
    for (const f of limited) {
      if (!f.type.startsWith('image/')) continue;
      try {
        const dataUrl = await fileToCompressedDataURL(f);
        processed.push({ name: f.name, dataUrl });
      } catch {
        /* ignore */
      }
    }
    setFiles((prev) => [...prev, ...processed]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const validateUrl = (u) => /^https?:\/\/.+/i.test(u.trim());

  const analyze = async () => {
    if (!validateUrl(videoUrl)) {
      toast.error('請輸入有效的影片連結');
      return;
    }
    if (files.length === 0) {
      toast.error('請至少上傳 1 張 IG 後台截圖');
      return;
    }
    setLoading(true);
    try {
      // 1) Submit job
      const start = await axios.post(
        `${API}/api/scorecard/analyze`,
        {
          video_url: videoUrl.trim(),
          screenshots: files.map((f) => f.dataUrl),
        },
        { timeout: 60000 }
      );
      const jobId = start.data.job_id;
      if (!jobId) throw new Error('後端未回傳任務 ID');

      // 2) Poll every 2s, up to 5 minutes
      const maxTries = 150;
      let tries = 0;
      while (tries < maxTries) {
        await new Promise((r) => setTimeout(r, 2000));
        tries += 1;
        try {
          const res = await axios.get(`${API}/api/scorecard/job/${jobId}`, { timeout: 15000 });
          const status = res.data.status;
          if (status === 'done') {
            setReport(res.data.report);
            setMeta({
              credits_charged: res.data.credits_charged,
              credits_remaining: res.data.credits_remaining,
              model: res.data.model,
              id: res.data.scorecard_id,
            });
            setStep(3);
            toast.success('診斷完成');
            return;
          }
          if (status === 'failed') {
            throw new Error(res.data.error || '後端分析失敗');
          }
          // still processing → keep polling
        } catch (pollErr) {
          // If we hit a transient error we retry. Break only on definitive failure.
          if (pollErr.response?.status === 404) {
            throw pollErr;
          }
          // otherwise keep polling silently
        }
      }
      throw new Error('診斷超時（超過 5 分鐘未完成）');
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || e.message || '診斷失敗';
      toast.error(`診斷失敗：${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#0E1014',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      link.download = `reelscript-scorecard-${aspect.replace(':', 'x')}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('已下載 PNG');
    } catch (e) {
      console.error(e);
      toast.error('下載失敗');
    }
  };

  const reset = () => {
    setStep(1);
    setVideoUrl('');
    setFiles([]);
    setReport(null);
    setMeta(null);
    setAspect('1:1');
  };

  // Auto-load report from ?open=<id> (for /scorecard/history deep-link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get('open');
    if (!openId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/scorecard/${openId}`);
        const doc = r.data;
        if (doc?.report) {
          setReport(doc.report);
          setVideoUrl(doc.video_url || '');
          setMeta({
            credits_charged: Number(doc.credits_charged || 0),
            credits_remaining: Number(user?.credits || 0),
            model: doc.model || '',
          });
          setStep(3);
        }
      } catch (_) {
        toast.error('讀取診斷報告失敗');
      } finally {
        window.history.replaceState({}, '', '/scorecard');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-theme="dark" data-atelier="true" className="flex h-screen bg-obsidian-900 text-white">
      <MainNavigation />

      <div className="flex-1 flex flex-col overflow-hidden grain-dark">
        <header className="border-b border-white/[0.06] px-6 md:px-10 py-5 flex-shrink-0">
          <div className="max-w-6xl mx-auto flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-lime-500" strokeWidth={2} />
            <span className="label-micro text-lime-500">VIDEO · DIAGNOSE</span>
            <span className="h-3.5 w-px bg-white/10" />
            <h1 className="font-display font-semibold tracking-tight text-white text-xl md:text-2xl">
              影片診斷報告
            </h1>
            <button
              onClick={openHistoryDrawer}
              className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/[0.04] border border-white/10 hover:border-lime-500/50 hover:bg-lime-500/[0.06] text-sm text-obsidian-100 transition-colors"
              data-testid="scorecard-history-btn"
            >
              <History className="w-4 h-4 text-lime-500" strokeWidth={2} />
              <span className="hidden md:inline">報告歷史</span>
            </button>
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto px-6 md:px-10 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <Stepper step={step} />

            {/* STEP 1: URL */}
            {step === 1 && (
              <div className="max-w-2xl">
                <div className="atelier-caption mb-3">STEP · 01 · 貼上影片連結</div>
                <h2 className="font-display font-semibold text-3xl md:text-4xl text-white tracking-tight leading-tight mb-3">
                  貼上要診斷的<span className="text-lime-500">影片連結</span>
                </h2>
                <p className="text-obsidian-200 text-sm md:text-base mb-8">
                  可以是 Instagram Reels、TikTok、YouTube Shorts 連結。之後會帶入這支影片的逐字稿來做數據 × 內容的交叉分析。
                </p>

                <div className="relative">
                  <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-atelier-subtle" />
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.instagram.com/reel/..."
                    className="w-full bg-atelier-surface border border-atelier-line focus:border-atelier-coral/60 rounded-xl pl-11 pr-4 py-4 text-base text-atelier-ink placeholder:text-atelier-subtle outline-none transition-colors"
                    data-testid="scorecard-url-input"
                  />
                </div>

                <div className="flex justify-end mt-8">
                  <button
                    onClick={() => {
                      if (!validateUrl(videoUrl)) {
                        toast.error('請輸入有效的影片連結');
                        return;
                      }
                      setStep(2);
                    }}
                    className="btn-neon inline-flex items-center gap-2 px-6 h-11 rounded-full text-sm font-medium"
                    data-testid="scorecard-next-1"
                  >
                    下一步 <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: Upload */}
            {step === 2 && (
              <div>
                <div className="atelier-caption mb-3">STEP · 02 · 上傳 IG 後台截圖</div>
                <h2 className="font-display font-semibold text-3xl md:text-4xl text-white tracking-tight leading-tight mb-3">
                  上傳 IG 後台截圖 <span className="text-atelier-subtle text-lg font-normal ml-2">{files.length} / {MAX_FILES}</span>
                </h2>
                <p className="text-obsidian-200 text-sm md:text-base mb-8 max-w-2xl">
                  在 Instagram 點進影片 → 右下「查看洞察報告」→ 盡可能截下：完播率曲線、互動分佈、非粉絲觸及、觀眾年齡/性別、地區…。AI 會用視覺讀取數字。
                </p>

                <div
                  onDrop={onDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/[0.12] rounded-2xl p-10 text-center cursor-pointer hover:border-lime-500/40 transition-colors bg-white/[0.02]"
                  data-testid="scorecard-dropzone"
                >
                  <div className="mx-auto w-12 h-12 rounded-full bg-lime-500/10 border border-lime-500/30 flex items-center justify-center mb-4">
                    <Upload className="w-5 h-5 text-lime-500" strokeWidth={1.8} />
                  </div>
                  <p className="text-white font-medium mb-1">拖放截圖到這裡，或點擊選擇</p>
                  <p className="text-xs text-atelier-subtle">支援 PNG / JPG · 最多 {MAX_FILES} 張 · 自動壓縮到 1920px</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      handleFiles(e.target.files);
                      e.target.value = '';
                    }}
                    data-testid="scorecard-file-input"
                  />
                </div>

                {/* Thumbnails */}
                {files.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mt-6">
                    {files.map((f, i) => (
                      <div
                        key={i}
                        className="relative group rounded-lg overflow-hidden bg-obsidian-900 border border-white/10 aspect-[3/4]"
                        data-testid={`scorecard-thumb-${i}`}
                      >
                        <img src={f.dataUrl} alt={f.name} className="w-full h-full object-cover" />
                        <button
                          onClick={() => removeFile(i)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`scorecard-thumb-remove-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-2 py-1 text-[10px] text-white truncate">
                          {f.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between items-center mt-8">
                  <button
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-2 text-sm text-atelier-mute hover:text-atelier-ink"
                  >
                    <ArrowLeft className="w-4 h-4" /> 上一步
                  </button>
                  <button
                    onClick={analyze}
                    disabled={loading || files.length === 0}
                    className="btn-neon inline-flex items-center gap-2 px-6 h-11 rounded-full text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="scorecard-analyze-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> AI 分析中…
                      </>
                    ) : (
                      <>
                        開始診斷 <Sparkles className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
                {loading && (
                  <p className="text-xs text-atelier-subtle mt-4 text-right">
                    AI 正在讀取 {files.length} 張截圖 · 通常 30–90 秒，慢的話最多 2–3 分鐘
                  </p>
                )}
              </div>
            )}

            {/* STEP 3: Report */}
            {step === 3 && report && (
              <ReportView
                report={report}
                meta={meta}
                user={user}
                aspect={aspect}
                setAspect={setAspect}
                cardRef={cardRef}
                downloadCard={downloadCard}
                reset={reset}
              />
            )}
          </div>
        </main>
      </div>

      {historyOpen && (
        <ScorecardHistoryDrawer
          history={history}
          loading={loadingHistory}
          onClose={() => setHistoryOpen(false)}
          onOpen={loadReport}
        />
      )}
    </div>
  );
};

const ReportView = ({ report, meta, user, aspect, setAspect, cardRef, downloadCard, reset }) => {
  const v = report.verdict || {};
  const m = report.metrics || {};
  const curve = report.retention_curve || [];
  const ageGender = report.demographics?.age_gender || [];
  const locations = report.demographics?.top_locations || [];
  const scores = report.radar_scores || {};
  const insights = report.insights || {};
  const freeNarrative = (report.free_narrative || '').trim();

  const radarData = [
    { k: 'Hook',  v: Number(scores.hook || 0) },
    { k: '留存',   v: Number(scores.retention || 0) },
    { k: '互動',   v: Number(scores.engagement || 0) },
    { k: '觸及',   v: Number(scores.reach || 0) },
    { k: '分享',   v: Number(scores.share_rate || 0) },
  ];

  const ageGenderFlat = ageGender.map((r) => ({
    bucket: r.bucket,
    女性: Number(r.female_pct || 0),
    男性: Number(r.male_pct || 0),
  }));

  return (
    <div>
      <div className="atelier-caption mb-3">STEP · 03 · 診斷結果</div>
      <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
        <div>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-4xl md:text-5xl">{v.emoji || '🔥'}</span>
            <h2 className="font-display font-semibold tracking-tighter text-lime-500 text-4xl md:text-5xl lg:text-6xl">
              「{v.tag || '—'}」
            </h2>
          </div>
          {v.one_liner && (
            <p className="text-white text-lg md:text-xl max-w-2xl leading-relaxed">{v.one_liner}</p>
          )}
          {meta && (
            <div className="mt-4 atelier-caption">
              本次使用 <span className="text-lime-500">{meta.credits_charged?.toFixed(2)} 點數</span>
              {' · '}剩餘 {meta.credits_remaining?.toFixed(2)}
              {' · '}MODEL: {meta.model}
            </div>
          )}
        </div>
        <button
          onClick={reset}
          className="inline-flex items-center gap-2 text-sm text-atelier-mute hover:text-atelier-coral transition-colors"
          data-testid="scorecard-new-btn"
        >
          <Sparkles className="w-3.5 h-3.5" /> 分析另一支影片
        </button>
      </div>

      {/* Core metrics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.08] mb-8">
        <MetricBlock label="COMPLETION" suffix="%" value={m.completion_rate_pct} />
        <MetricBlock label="AVG · WATCH" suffix="s" value={m.avg_watch_time_sec} />
        <MetricBlock label="ENGAGEMENT" suffix="%" value={m.engagement_rate_pct} />
        <MetricBlock label="NON · FOLLOWER" suffix="%" value={m.non_follower_reach_pct} />
      </div>

      {/* Charts row */}
      <div className="grid md:grid-cols-[1fr_360px] gap-5 mb-8">
        <Panel title="RETENTION · CURVE · 留存曲線">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={curve}>
                <defs>
                  <linearGradient id="retFull" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF5436" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FF5436" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="t_sec" tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} unit="s" />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#7A7E86' }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickLine={false} unit="%" />
                <Tooltip contentStyle={{ background: '#0E1014', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="retention_pct" name="留存率" stroke="#FF5436" strokeWidth={2} fill="url(#retFull)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="HEALTH · RADAR · 五維健康">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.12)" />
                <PolarAngleAxis dataKey="k" tick={{ fontSize: 11, fill: '#C8CCD1' }} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} axisLine={false} />
                <Radar dataKey="v" stroke="#FF5436" strokeWidth={2} fill="#FF5436" fillOpacity={0.4} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      {/* Demographics row */}
      <div className="grid md:grid-cols-2 gap-5 mb-8">
        <Panel title="AUDIENCE · AGE × GENDER · 觀眾年齡性別">
          <div style={{ height: 220 }}>
            {ageGenderFlat.length === 0 ? (
              <EmptyHint text="無年齡性別數據" />
            ) : (
              <ResponsiveContainer>
                <BarChart data={ageGenderFlat}>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: '#7A7E86' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#7A7E86' }} />
                  <Tooltip contentStyle={{ background: '#0E1014', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="女性" fill="#FF5436" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="男性" fill="#60A5FA" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Panel>

        <Panel title="TOP · REGIONS · 地區分佈">
          {locations.length === 0 ? (
            <EmptyHint text="無地區數據" />
          ) : (
            <div className="space-y-3 py-2">
              {locations.slice(0, 8).map((l, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-16 text-sm text-obsidian-100">{l.region}</div>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full bg-lime-500 rounded-full"
                      style={{ width: `${Math.min(l.pct, 100)}%` }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm text-white tabular-nums">
                    {Number(l.pct || 0).toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* AI Insights trio */}
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        <InsightCard tone="lime" label="✨ HIGHLIGHT · 亮點" text={insights.highlight} />
        <InsightCard tone="amber" label="⚠ OPTIMIZATION · 待修正" text={insights.optimization} />
        <InsightCard tone="lime-light" label="→ NEXT · 下次試試" text={insights.next_try} />
      </div>

      {/* DEEP DIVE · 導師專欄 (markdown free narrative) */}
      {freeNarrative && (
        <div
          className="border border-white/[0.06] bg-white/[0.02] rounded-2xl px-6 py-7 md:px-9 md:py-10 mb-10"
          data-testid="scorecard-deep-dive"
        >
          <div className="flex items-center gap-3 mb-5">
            <Sparkles className="w-4 h-4 text-lime-500" strokeWidth={2} />
            <span className="label-micro text-lime-500">DEEP · DIVE</span>
            <span className="h-3.5 w-px bg-white/10" />
            <h3 className="font-display font-semibold text-white text-xl md:text-2xl tracking-tight">
              導師專欄
            </h3>
          </div>
          <div className="scorecard-deep-dive-md max-w-none">
            <MarkdownContent content={freeNarrative} />
          </div>
        </div>
      )}

      {/* Share card zone */}
      <div className="border-t border-white/[0.06] pt-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="label-micro text-lime-500 mb-2">SHAREABLE · CARD</div>
            <h3 className="font-display font-semibold text-white text-2xl tracking-tight">
              截下來發 Story 或貼文
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-white/[0.04] border border-white/10 rounded-full p-1">
              <button
                onClick={() => setAspect('1:1')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  aspect === '1:1' ? 'bg-lime-500 text-obsidian-900' : 'text-obsidian-200 hover:text-white'
                }`}
                data-testid="scorecard-aspect-1x1"
              >
                1:1 · 貼文
              </button>
              <button
                onClick={() => setAspect('9:16')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  aspect === '9:16' ? 'bg-lime-500 text-obsidian-900' : 'text-obsidian-200 hover:text-white'
                }`}
                data-testid="scorecard-aspect-9x16"
              >
                9:16 · Story
              </button>
            </div>
            <button
              onClick={downloadCard}
              className="inline-flex items-center gap-2 bg-lime-500 text-obsidian-900 h-9 px-4 rounded-full text-xs font-semibold hover:scale-[1.02] transition-transform"
              data-testid="scorecard-download-btn"
            >
              <Download className="w-3.5 h-3.5" /> 下載 PNG
            </button>
          </div>
        </div>

        <div className="flex justify-center py-6 overflow-auto">
          <ShareableCard
            ref={cardRef}
            report={report}
            aspect={aspect}
            handle={user?.name || user?.email?.split('@')[0]}
          />
        </div>
      </div>
    </div>
  );
};

const MetricBlock = ({ label, value, suffix }) => (
  <div className="bg-obsidian-900 p-5 md:p-6">
    <div
      className="atelier-caption mb-2"
      style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
    >
      {label}
    </div>
    <div className="font-display font-semibold tabular-nums text-white text-3xl md:text-4xl">
      {value === null || value === undefined ? (
        <span className="text-atelier-subtle">—</span>
      ) : (
        <>
          {Number(value).toFixed(1)}
          <span className="text-lime-500 text-xl md:text-2xl ml-0.5">{suffix}</span>
        </>
      )}
    </div>
  </div>
);

const Panel = ({ title, children }) => (
  <div className="bg-obsidian-900 border border-white/[0.08] rounded-2xl p-5 md:p-6">
    <div
      className="atelier-caption mb-4 flex items-center gap-2"
      style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
    >
      <span className="w-4 h-px bg-lime-500/60" />
      {title}
    </div>
    {children}
  </div>
);

const EmptyHint = ({ text }) => (
  <div className="h-full flex items-center justify-center text-xs text-atelier-mute">
    <ImageIcon className="w-4 h-4 mr-2 opacity-60" />
    {text}
  </div>
);

const InsightCard = ({ label, text, tone }) => {
  const toneMap = {
    lime: 'border-lime-500/40 bg-lime-500/[0.03]',
    amber: 'border-amber-400/30 bg-amber-500/[0.03]',
    'lime-light': 'border-lime-300/30 bg-lime-300/[0.03]',
  };
  return (
    <div className={`rounded-2xl border p-5 md:p-6 ${toneMap[tone] || ''}`}>
      <div
        className="text-[10px] tracking-[0.2em] text-lime-500 mb-3"
        style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
      >
        {label}
      </div>
      <p className="text-white/95 text-sm md:text-base leading-relaxed">
        {text || <span className="text-atelier-subtle">—</span>}
      </p>
    </div>
  );
};

export default Scorecard;

const ScorecardHistoryDrawer = ({ history, loading, onClose, onOpen }) => {
  const sorted = [...history].sort(
    (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
  );

  const verdictStr = (v) => {
    // report.verdict may be string OR {tag, emoji, one_liner}
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.tag || v.one_liner || '';
    return '';
  };
  const verdictLabel = (v) => {
    const s = verdictStr(v);
    if (!s) return '';
    const map = { excellent: '極佳', good: '不錯', avg: '一般', poor: '欠佳', fail: '未達標' };
    return map[s.toLowerCase()] || s;
  };
  const verdictTone = (v) => {
    const key = verdictStr(v).toLowerCase();
    if (['excellent', 'good'].includes(key)) return 'text-lime-400';
    if (['avg'].includes(key)) return 'text-amber-300';
    if (!key) return 'text-atelier-mute';
    return 'text-red-300';
  };
  const titleOf = (doc) => {
    const rep = doc.report || {};
    if (typeof rep.title === 'string' && rep.title) return rep.title;
    if (typeof rep.hook_summary === 'string' && rep.hook_summary) return rep.hook_summary;
    // verdict.one_liner is a good fallback single-sentence summary
    if (rep.verdict && typeof rep.verdict === 'object' && rep.verdict.one_liner) {
      return rep.verdict.one_liner;
    }
    return doc.video_url || '(未命名影片)';
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="scorecard-history-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <aside className="w-full max-w-md bg-obsidian-950 border-l border-obsidian-700 h-full overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-obsidian-700 sticky top-0 bg-obsidian-950 z-10">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-4 h-4 text-lime-500" />
            <h3 className="font-display text-lg text-white">診斷歷史</h3>
            <span className="text-[11px] text-atelier-mute">（{sorted.length}）</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-obsidian-800 rounded"
            data-testid="scorecard-history-close"
          >
            <X className="w-4 h-4 text-obsidian-300" />
          </button>
        </div>

        <div className="flex-1 p-3 space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-10 text-obsidian-400 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              讀取中…
            </div>
          )}

          {!loading && sorted.length === 0 && (
            <p className="text-sm text-obsidian-500 text-center py-10">
              仲未有診斷報告。貼連結 + 上傳截圖開始第一份。
            </p>
          )}

          {!loading &&
            sorted.map((doc) => {
              const rep = doc.report || {};
              const score = rep.overall_score ?? rep.score ?? null;
              const verdict = rep.verdict;
              const dateStr = doc.created_at
                ? new Date(doc.created_at).toLocaleString('zh-TW', {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })
                : '';
              const vLabel = verdictLabel(verdict);
              return (
                <button
                  key={doc.id}
                  onClick={() => onOpen(doc.id)}
                  className="w-full text-left bg-obsidian-900 border border-obsidian-700 rounded-lg p-3 hover:border-lime-500/50 transition-colors block"
                  data-testid={`scorecard-history-item-${doc.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {vLabel && (
                      <span className={`text-[10px] font-semibold tabular-nums ${verdictTone(verdict)}`}>
                        {vLabel}
                      </span>
                    )}
                    {score != null && (
                      <span className="text-[10px] text-obsidian-400 tabular-nums">
                        {Number(score).toFixed(0)} 分
                      </span>
                    )}
                    {typeof doc.credits_charged === 'number' && doc.credits_charged > 0 && (
                      <span className="text-[10px] text-obsidian-500 tabular-nums ml-auto">
                        {doc.credits_charged.toFixed(1)} cr
                      </span>
                    )}
                  </div>
                  <h4 className="text-sm text-white font-medium truncate">{titleOf(doc)}</h4>
                  {doc.video_url && (
                    <p className="text-[11px] text-obsidian-500 truncate mt-0.5">
                      {doc.video_url}
                    </p>
                  )}
                  <p className="text-[11px] text-obsidian-500 mt-0.5">{dateStr}</p>
                </button>
              );
            })}
        </div>
      </aside>
    </div>
  );
};
